import { useMemo } from 'react';
import {
  calculateMinEntryValue,
  calculatePaymentRules,
  getCreditCardFees,
  calculateCreditCardInstallmentValue,
  validateInstallmentMinValue
} from '../helpers/payment-rules.helpers';

interface UsePaymentRulesProps {
  totalValue: number;
  paymentMethod: string;
  installments: number;
  entryPercentage?: number;
}

export const usePaymentRules = ({ totalValue, paymentMethod, installments, entryPercentage = 0 }: UsePaymentRulesProps) => {
  // garantir número (pode vir string das opções)
  const entryPct = Number(entryPercentage) || 0;

  const rules = useMemo(() => {
    return calculatePaymentRules(totalValue, paymentMethod);
  }, [totalValue, paymentMethod]);

  const currentFees = useMemo(() => {
    if (paymentMethod === 'CREDIT_CARD') {
      return getCreditCardFees(installments);
    }
    return { rate: 0, fixed: 0, description: "Sem taxa" };
  }, [paymentMethod, installments]);

  const minEntryValue = useMemo(() => {
    if (!rules.requiresEntry) return 0;
    if (entryPct === 0) return 0;
    return calculateMinEntryValue(totalValue, rules.minEntryPercentage);
  }, [totalValue, rules.minEntryPercentage, rules.requiresEntry, entryPct]);

  const isValidInstallment = useMemo(() => {
    if (paymentMethod === 'CREDIT_CARD' || !rules.requiresEntry) return true;
    if (entryPct === 0) {
      return validateInstallmentMinValue(totalValue, installments);
    }
    const entryValue = (entryPct / 100) * totalValue;
    const remainingValue = totalValue - entryValue;
    return validateInstallmentMinValue(remainingValue, installments);
  }, [paymentMethod, rules.requiresEntry, entryPct, totalValue, installments]);

  const creditCardInstallmentOptions = useMemo(() => {
    if (paymentMethod !== 'CREDIT_CARD' || !rules.canInstallment) {
      return [{ value: "1", label: "1x" }];
    }

    const entryValue = (entryPercentage / 100) * totalValue;
    const remainingValue = totalValue - entryValue;

    const options: { value: string; label: string; }[] = [];


    const maxInstallments = Math.min(rules.maxInstallments, 12);

    for (let i = 1; i <= maxInstallments; i++) {
      const cardValue = remainingValue > 0 ? remainingValue : totalValue;
      const installmentValue = calculateCreditCardInstallmentValue(cardValue, i);
      const fees = getCreditCardFees(i);

      const label = entryPercentage > 0
        ? `${i}x de R$ ${installmentValue.toFixed(2)} (no cartão ${i > 1 ? `- Juros: ${fees.rate}% a.m.` : ''})`
        : `${i}x de R$ ${installmentValue.toFixed(2)} ${i > 1 ? `(Juros: ${fees.rate}% a.m.)` : ''}`;

      options.push({
        value: i.toString(),
        label
      });
    }

    return options;
  }, [paymentMethod, rules.canInstallment, rules.maxInstallments, totalValue, entryPercentage]);

  const pixBoletoInstallmentOptions = useMemo(() => {
    const placeholder = [{ value: "-", label: "Selecione o número de parcelas" }];

    if (totalValue < 50) {
      return [
        ...placeholder,
        { value: "1", label: "1x (À vista obrigatório)" }
      ];
    }

    if (totalValue <= 100) {
      return [
        ...placeholder,
        { value: "1", label: "1x (Valor muito baixo para parcelar)" }
      ];
    }

    // Sem entrada: calcular parcelas sobre o total (incluir 1x)
    if (entryPct === 0) {
      const options: { value: string; label: string; }[] = [...placeholder];
      const maxPossibleInstallments = Math.max(1, Math.floor(totalValue / 50));
      const finalMaxInstallments = Math.min(maxPossibleInstallments, rules.maxInstallments);

      for (let i = 1; i <= finalMaxInstallments; i++) {
        const installmentValue = totalValue / i;
        options.push({
          value: i.toString(),
          label: `${i}x de R$ ${(installmentValue.toFixed(2))}`
        });
      }
      return options;
    }

    // Se não requer entrada ou se é à vista (100%) — incluir 1x
    if (!rules.requiresEntry || entryPct === 100) {
      return [
        ...placeholder,
        { value: "1", label: `1x de R$ ${totalValue.toFixed(2)}` }
      ];
    }

    // Lógica original para quando há entrada (> 0) — sempre incluir 1x
    const entryValue = Math.max((entryPct / 100) * totalValue, 50);
    const remainingValue = totalValue - entryValue;

    const options = [
      ...placeholder,
      { value: "1", label: `1x de R$ ${totalValue.toFixed(2)}` }
    ];

    if (remainingValue < 50) {
      return options;
    }

    const maxPossibleInstallments = Math.floor(remainingValue / 50);
    const finalMaxInstallments = Math.min(maxPossibleInstallments, rules.maxInstallments);

    for (let i = 2; i <= finalMaxInstallments; i++) {
      const installmentValue = remainingValue / i;
      options.push({
        value: i.toString(),
        label: `${i}x de R$ ${installmentValue.toFixed(2)}`
      });
    }

    return options;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules.requiresEntry, rules.maxInstallments, entryPct, totalValue, rules.canInstallment]);

  const installmentOptions = paymentMethod === 'CREDIT_CARD'
    ? creditCardInstallmentOptions
    : pixBoletoInstallmentOptions;

  const creditCardEntryOptions = useMemo(() => {
    return [
      { value: "0", label: "Sem entrada" },
      { value: "10", label: "10%" },
      { value: "20", label: "20%" },
      { value: "30", label: "30%" },
      { value: "40", label: "40%" },
      { value: "50", label: "50%" },
      { value: "60", label: "60%" },
      { value: "70", label: "70%" },
      { value: "80", label: "80%" },
      { value: "90", label: "90%" },
      { value: "100", label: "100% (À vista)" }
    ];
  }, []);

  const entryPercentageOptions = useMemo(() => {
    if (paymentMethod === 'CREDIT_CARD') {
      return creditCardEntryOptions;
    }

    if (!rules.requiresEntry) {
      return [];
    }

    if (totalValue < 50) {
      return [
        { value: "-", label: "-" },
        { value: "0", label: "Sem entrada" }, // Disponível sempre
        { value: "100", label: "100% (À vista obrigatório)" }
      ];
    }

    if (totalValue <= 100) {
      return [
        { value: "-", label: "-" },
        { value: "0", label: "Sem entrada" }, // Disponível sempre
        { value: "100", label: "100% (À vista - valor muito baixo)" }
      ];
    }

    // Para qualquer valor > 100, sempre incluir "Sem entrada"
    const baseOptions = [
      { value: "-", label: "-" },
      { value: "0", label: "Sem entrada" }, // Sempre disponível
      { value: "100", label: "100% (À vista)" }
    ];

    if (rules.canInstallment) {
      const minPercentage = rules.minEntryPercentage;
      const minEntryValue = Math.max((minPercentage / 100) * totalValue, 50);
      const remainingValue = totalValue - minEntryValue;

      if (remainingValue >= 50 && minPercentage > 0) {
        baseOptions.splice(-1, 0,
          { value: minPercentage.toString(), label: `${minPercentage}% (Mínimo - entrada R$ ${minEntryValue.toFixed(2)})` }
        );

        const additionalOptions = [50, 60, 70, 80, 90];
        additionalOptions.forEach(percentage => {
          const entryValue = Math.max((percentage / 100) * totalValue, 50);
          const remaining = totalValue - entryValue;

          if (percentage > minPercentage && percentage < 100 && remaining >= 50) {
            baseOptions.splice(-1, 0, {
              value: percentage.toString(),
              label: `${percentage}% (entrada R$ ${entryValue.toFixed(2)})`
            });
          }
        });
      }
    }

    return baseOptions;
  }, [rules.canInstallment, rules.minEntryPercentage, rules.requiresEntry, paymentMethod, creditCardEntryOptions, totalValue]);
  return {
    rules,
    currentFees,
    minEntryValue,
    installmentOptions,
    entryPercentageOptions,
    isValidInstallment
  };
};